/**
 * public/js/tool-draft-editor.js — «my work» for an editor, on top of tool-draft.js.
 *
 *   var ed = window.__toolDraft.editor({ slug, payload, apply, fresh, started, t, track, ui, … });
 *   ed.touch(counted) · ed.exported() · ed.boot() → Promise · ed.quietly(fn) · ed.reset()
 *   ed.adopt(edits) — the page OPENED with a work (an older save of the tool, a shared
 *   link) rather than the person beginning one: after boot() it becomes a saved work
 *   ed.leave() — the page moves on to another work by itself (the next file of a tool
 *   whose work is a run's result): the one it had stays in the list, the page is not cleared
 *
 * What an editor gets (each decision was proven on /thumbnail-maker in a real
 * browser — a hard kill of Chrome, F5 right after a change, two tabs, a tab that
 * answers late, an eviction — and is explained in docs/why/primitives/tool-draft.md):
 *   - every change is saved: debounced, flushed when the tab is hidden or goes away;
 *   - the work that was being edited when the page died reopens by itself on the
 *     next visit, with a notice; F5 brings back what THIS tab had, whatever its state;
 *   - «My work»: the list to open or delete, with which one another tab has open
 *     and which were downloaded; «New» keeps the current one in the list;
 *   - two tabs never overwrite each other: the stronger claim keeps the work, a late
 *     «mine» before any edit here gives it back, after edits this tab goes on as a copy.
 *
 * The tool supplies:
 *   payload()  → {state, files?, fileMeta?, label?, preview?, meta?} | null while not ready
 *              (files: File/Blob for every picture or recording the work needs)
 *   apply(d)   → Promise<string[]> — put a saved work on the page; resolves with the
 *              names of files that did not come back (they are dropped from the work)
 *   fresh()    — clear the page to a new, empty work (undo history included)
 *   started()  → the person has already begun something on this page: an unfinished
 *              work from before does not replace it on arrival
 *   t(key, fallback), track(action, data), toast(msg, type), trackError(err, ctx)
 *   ui: {saveState, note, noteText, noteNew, noteList, noteClose, listBtn, count, pop, list, newBtn}
 *   cls: class prefix of the list rows ('td' → .td-draft, .td-draft-nm …)
 *   keys: renames of the string keys (defaults below are /thumbnail-maker's)
 *   openKey: sessionStorage key of «what this tab has open» (default 'td-open:<slug>')
 *   reopenUnfinished: false — a NEW tab never opens a saved work by itself. For a tool
 *              whose work is the RESULT of a run (a transcription): the next visit is
 *              usually for another file, so the saved result is offered, not put on the
 *              page. The tab's own work still comes back after F5 or a return to the page.
 */
(function () {
'use strict';
if (!window.__toolDraft || window.__toolDraft.editor) return;

var DAY = 864e5;
var KEYS = {
    saved: 'draft_saved', saveFailed: 'draft_save_failed', noRoom: 'draft_no_room',
    openElsewhere: 'draft_open_elsewhere', forked: 'draft_forked', openFailed: 'draft_open_failed',
    filesMissing: 'draft_images_missing', restored: 'draft_restored', lost: 'draft_lost',
    current: 'draft_current', otherTab: 'draft_other_tab', exported: 'draft_exported',
    notExported: 'draft_not_exported', untitled: 'draft_untitled', del: 'draft_delete',
    empty: 'drafts_empty', confirmReset: 'confirm_reset', usage: 'drafts_usage',
    trimmed: 'drafts_trimmed', trimmedRow: 'drafts_trimmed_row', budget: 'drafts_budget',
    budgetUnlimited: 'drafts_budget_unlimited', budgetNote: 'drafts_budget_note',
    protectedYes: 'drafts_protected_yes', protectedNo: 'drafts_protected_no'
};
var FALLBACK = {
    saved: 'Saved in this browser', saveFailed: 'Could not save this in this browser',
    noRoom: 'This browser has no room for the files of this work — the work itself is saved, the files would have to be added again.',
    openElsewhere: 'This is already open in another tab — continue there, or start something new here.',
    forked: 'This is also open in another tab — your changes here are saved as a copy.',
    openFailed: 'Could not open this.', filesMissing: 'Some files of this work were not saved — add them again.',
    restored: 'Your work from :time is back — continue where you left off.',
    lost: 'The browser deleted saved work to free up disk space.',
    current: 'open now', otherTab: 'open in another tab', exported: 'downloaded', notExported: 'not downloaded yet',
    untitled: 'Untitled', del: 'Delete', empty: 'Your work is saved here automatically.',
    confirmReset: 'Clear everything and start over?',
    usage: 'Using :size in this browser.',
    budget: 'Room for files:', budgetUnlimited: 'no limit',
    budgetNote: 'Past it, the oldest works stop keeping their files — the works themselves stay.',
    protectedYes: 'The browser will not delete them by itself when the disk runs low.',
    protectedNo: 'If the disk runs out of space, the browser may delete them by itself.',
    trimmed: 'To make room for new work, the files of this one were not kept — add them again; everything else is here.',
    trimmedRow: 'files need adding again'
};

function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
}

// The page settles before «is it open in another tab?» is asked: the tab that has
// it is not in front and answers when the browser lets it run (docs/why).
function whenIdle(ms) {
    return new Promise(function (resolve) {
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(function () { resolve(); }, { timeout: ms });
        else setTimeout(resolve, Math.min(ms, 800));
    });
}

// The strings resources/views/partials/tool-drafts.blade.php renders into data-td-i18n.
var BAG = {
    saved: 'saved', saveFailed: 'save_failed', noRoom: 'no_room', openElsewhere: 'open_elsewhere',
    forked: 'forked', openFailed: 'open_failed', filesMissing: 'files_missing', restored: 'restored',
    lost: 'lost', current: 'current', otherTab: 'other_tab', exported: 'exported',
    notExported: 'not_exported', untitled: 'untitled', del: 'delete', empty: 'empty',
    confirmReset: 'confirm_reset', usage: 'usage', trimmed: 'trimmed', trimmedRow: 'trimmed_row',
    budget: 'budget', budgetUnlimited: 'budget_unlimited', budgetNote: 'budget_note',
    protectedYes: 'protected_yes', protectedNo: 'protected_no'
};

/* root: the id the shared partial was included with — its parts are root+'New',
   root+'ListBtn', root+'Count', root+'Pop', root+'List', root+'Save', root+'Note',
   root+'NoteText', root+'NoteNew', root+'NoteList', root+'NoteX'; its strings are in
   data-td-i18n on the element with the root id itself. */
function fromRoot(root) {
    var rid = typeof root === 'string' ? root : root.id;
    var g = function (s) { return document.getElementById(rid + s); };
    var bag = {};
    try { bag = JSON.parse((document.getElementById(rid) || {}).getAttribute('data-td-i18n') || '{}'); } catch (_) {}
    return {
        ui: {
            saveState: g('Save'), note: g('Note'), noteText: g('NoteText'), noteNew: g('NoteNew'),
            noteList: g('NoteList'), noteClose: g('NoteX'), listBtn: g('ListBtn'), count: g('Count'),
            pop: g('Pop'), list: g('List'), newBtn: g('New')
        },
        bag: bag
    };
}

function editor(o) {
    var slug = o.slug;
    /* No count cap unless the tool asks for one. Until 18.10.2026 the default was 12,
       and the thirteenth work silently deleted the oldest — with its files: report
       #9299 (/audio-to-video) had made ~40 clips and saw the newest 12 («Неужели так
       должно быть?»). The cap stood in for disk space; the store now keeps each file
       once however many works hold it (tool-draft.js, content ids), the list says
       what the works take, and nothing disappears that the person did not delete or
       leave untouched for AGE. */
    /* …and no deletion by age unless the tool asks for it: «хочу хранить все клипы»
       (#9299) — a clip untouched for 60 days used to vanish whole. The byte budget
       below is what keeps the disk in check; a work's state is a few kilobytes. */
    var MAX = o.maxDrafts || 0, AGE = o.maxAgeMs || 0, REOPEN = o.reopenMs || 14 * DAY;
    /* Past this many bytes (each shared file once) the oldest works stop holding their
       files — they stay in the list and ask for them on opening (tool-draft.js _trim).
       1 GiB keeps every work whole for 99 % of people: on /audio-to-video the p99 of
       what one person made in 17 days is 437 MB. The person may choose more (or no
       limit) under the list — #9299, a musician: «хочу хранить все клипы и готов
       мириться, если это будет занимать много места» — remembered on this device. */
    var GIB = 1024 * 1024 * 1024;
    var BUDGET_DEFAULT = o.bytesBudget || GIB;
    var BUDGET_KEY = 'td-budget:' + slug;
    var BUDGET = (function () {
        try {
            var v = localStorage.getItem(BUDGET_KEY);
            if (v !== null && v !== '' && isFinite(+v) && +v >= 0) return +v;
        } catch (_) {}
        return BUDGET_DEFAULT;
    })();
    var R = o.root ? fromRoot(o.root) : null;
    var ui = o.ui || (R && R.ui) || {}, cls = o.cls || 'td';
    var K = {}, k;
    for (k in KEYS) K[k] = KEYS[k];
    if (o.keys) for (k in o.keys) K[k] = o.keys[k];
    var LOCALE = o.locale || window.__LOCALE || document.documentElement.lang || 'en';
    function t(name) {
        var f = FALLBACK[name];
        if (R && R.bag[BAG[name]]) f = R.bag[BAG[name]];
        try { return o.t ? o.t(K[name], f) : f; } catch (_) { return f; }
    }
    // Every editor reports its work the same way — feature_use {action}, as
    // /thumbnail-maker did first — so one query counts all of them.
    function track(a, d) {
        try {
            if (o.track) { o.track(a, d || {}); return; }
            var p = { slug: slug, action: a }, x;
            if (d) for (x in d) p[x] = d[x];
            if (window.__ae) window.__ae('feature_use', p);
        } catch (_) {}
    }
    function toast(m, ty) { try { (o.toast || window.__toast || function () {})(m, ty); } catch (_) {} }
    function trackError(e, ctx) {
        try {
            if (o.trackError) { o.trackError(e, ctx); return; }
            if (window.__aeToolError) window.__aeToolError({ slug: slug, msg: String(e && (e.message || e) || '').slice(0, 200), ctx: ctx || 'unknown' });
        } catch (_) {}
    }

    var D = {
        store: null,       // null — not asked yet; false — unavailable in this browser
        id: null,          // the work on the page; null until its first change
        live: false,       // before boot the page holds its seed, not a work
        quiet: 0,          // > 0 while the page itself rebuilds the work
        edits: 0, editedAt: 0, exportedAt: 0,
        rows: [], degradedShown: false,
        auto: false,       // reopened on arrival, not picked by the person
        editsAtOpen: 0,    // D.edits when it was opened: equal = nothing of this tab in it yet
        busy: {},          // works other tabs have open, as heard while the list is open
        stopWatch: null    // stops that listening
    };

    // Which work THIS tab has open. sessionStorage belongs to the tab: it survives
    // F5 and Chrome's restore of the tab after a crash, and a new tab starts without it.
    var OPEN_KEY = o.openKey || ('td-open:' + slug);
    function rememberOpen(id) {
        try { if (id) sessionStorage.setItem(OPEN_KEY, id); else sessionStorage.removeItem(OPEN_KEY); } catch (_) {}
    }
    function lastOpen() { try { return sessionStorage.getItem(OPEN_KEY) || ''; } catch (_) { return ''; } }

    function store() {
        if (D.store !== null) return D.store;
        D.store = false;
        try {
            D.store = window.__toolDraft.store(slug, {
                maxDrafts: MAX, bytesBudget: BUDGET, maxAgeMs: AGE || Infinity, debounceMs: o.debounceMs || 500, onConflict: forked
            });
        } catch (_) {}
        return D.store;
    }
    function newId() { return 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }

    function quietly(fn) {
        D.quiet++;
        try { return fn(); } finally { D.quiet--; }
    }

    // A change of the work. counted — a committed step (history, typing), not a
    // slider in motion: only counted changes START a work and count as edits.
    function touch(counted) {
        if (!D.live || D.quiet) return;
        var S = store(); if (!S) return;
        if (!D.id) {
            if (!counted) return;
            D.id = newId();
            D.auto = false;
            S.claim(D.id);
            rememberOpen(D.id);
        }
        if (counted) { D.edits++; D.editedAt = Date.now(); }
        S.schedule(D.id, payload, saved);
    }

    function payload() {
        var p;
        try { p = o.payload(); } catch (e) { trackError(e, 'draft_payload'); return null; }
        if (!p) return null;
        var meta = {}, m;
        if (p.meta) for (m in p.meta) meta[m] = p.meta[m];
        meta.editedAt = D.editedAt; meta.exportedAt = D.exportedAt; meta.edits = D.edits;
        return { v: p.v || 1, state: p.state, files: p.files || [], fileMeta: p.fileMeta, label: p.label || '', preview: p.preview || '', meta: meta };
    }

    function saved(r) {
        if (!r) return;
        if (r.ok) {
            setSaveState('saved');
            // a work saved for the first time is one more in the list: its count says so
            // without waiting for the list to be opened
            if (D.id && !D.rows.some(function (x) { return x.id === D.id; })) refreshRows();
            if (r.degraded && !D.degradedShown) {
                D.degradedShown = true;
                toast(t('noRoom'), 'warning');
                track('draft_degraded', { why: String(r.degraded).slice(0, 30) });
            }
            return;
        }
        if (r.why === 'foreign') return;      // forked() takes it from here
        setSaveState('failed');
        track('draft_save_failed', { why: String(r.why || '').slice(0, 30) });
    }

    // Another tab has this work open, or wrote it after this page last saw it. Never
    // overwrite theirs. If this tab only REOPENED it on arrival and nothing of this tab
    // went into it yet, the work goes back to the tab that has it and this one starts
    // clean; otherwise it carries on under a new id, as a copy.
    function forked(id, how) {
        if (id !== D.id) return;
        var S = store(); if (!S) return;
        if (D.auto && D.edits === D.editsAtOpen) {
            S.cancelScheduled();
            S.release(id);
            D.id = null; D.auto = false; D.edits = 0; D.editedAt = 0; D.exportedAt = 0;
            rememberOpen(null);
            quietly(o.fresh);
            setSaveState('');
            showNotice(t('openElsewhere'), false);
            track('draft_yielded', { how: how });
            return;
        }
        D.id = newId();
        D.auto = false;
        S.claim(D.id);
        rememberOpen(D.id);
        D.editedAt = Date.now();
        S.schedule(D.id, payload, saved);
        toast(t('forked'), 'info');
        track('draft_forked', { how: how });
    }

    function exported() {
        var S = store(); if (!S || !D.live) return;
        if (!D.id) { D.id = newId(); S.claim(D.id); }
        D.exportedAt = Date.now();
        S.schedule(D.id, payload, saved);
    }

    // The page opened WITH a work instead of the person beginning one here — the
    // tool's older one-slot save, a shared link: it becomes a saved work at once, so
    // F5 and «New» treat it like any other. edits >= 2 makes it «unfinished», and it
    // reopens on the next visit the way the old save did; 0 keeps it in the list
    // only. Call it once boot() has decided (its promise): before that, a work that
    // should reopen would lose to this one.
    function adopt(edits) {
        var S = store(); if (!S || !D.live || D.id) return false;
        D.id = newId();
        D.auto = false;
        S.claim(D.id);
        rememberOpen(D.id);
        D.edits = edits || 0;
        D.editedAt = Date.now();
        S.schedule(D.id, payload, saved);
        return true;
    }

    // The page itself moves on to another work — a transcription tool was handed the next
    // file while a result was open. What it had stays in the list AS IT IS, and the page is
    // not cleared: it is already showing, or about to show, the next one. Unlike reset(),
    // the id is let go in this very task — the result of the new run may arrive before a
    // promise would resolve, and it must never be written into the work that was open.
    // The pending save is built from the page here (flush() calls the builder at once), so
    // call leave() BEFORE the page's state changes.
    function leave() {
        var S = store();
        if (!S || !D.live || !D.id) return false;
        var id = D.id;
        var letGo = function () { S.release(id); };
        S.flush().then(letGo, letGo);
        D.id = null; D.edits = 0; D.editedAt = 0; D.exportedAt = 0; D.auto = false;
        rememberOpen(null);
        hideNotice();
        setSaveState('');
        track('draft_left');
        return true;
    }

    function unfinished(row) {
        var m = row.meta || {};
        return (m.edits || 0) >= 2 && (m.editedAt || 0) > (m.exportedAt || 0)
            && Date.now() - (row.savedAt || 0) < REOPEN;
    }

    function time(ts) {
        try { return new Date(ts).toLocaleString(LOCALE, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
        catch (_) { return new Date(ts).toLocaleString(); }
    }

    function setSaveState(s) {
        var el = ui.saveState; if (!el) return;
        el.classList.toggle('bad', s === 'failed');
        if (s === 'saved') { el.textContent = '✓ ' + t('saved'); el.hidden = false; }
        else if (s === 'failed') { el.textContent = t('saveFailed'); el.hidden = false; }
        else el.hidden = true;
    }

    function showNotice(text, withNew) {
        if (!ui.note || !ui.noteText) return;
        ui.noteText.textContent = text;
        if (ui.noteNew) ui.noteNew.hidden = !withNew;
        ui.note.hidden = false;
    }
    function hideNotice() { if (ui.note) ui.note.hidden = true; }

    // opts.auto — reopening an unfinished work on arrival (not a click in the list)
    function open(id, opts) {
        opts = opts || {};
        var S = store(); if (!S) return Promise.resolve(false);
        // o.partialFiles: the tool puts back the files that came back and asks for the rest
        // (a file unreadable at saving time costs only itself — tool-draft.js _saveFileByFile)
        return Promise.all([S.load(id, { partial: !!o.partialFiles }), opts.busy ? Promise.resolve(opts.busy) : S.claimedElsewhere()]).then(function (r) {
            var d = r[0], busy = r[1];
            if (!d) { if (!opts.auto) toast(t('openFailed'), 'error'); return false; }
            // arriving: if the person has already started something, it is theirs
            if (opts.auto && (D.id || (o.started && o.started()))) return false;
            // the work on the page goes to the list as it is, then gives way
            return S.flush().then(function () {
                if (D.id) S.release(D.id);
                D.quiet++;
                return Promise.resolve().then(function () { return o.apply(d); }).then(function (missing) {
                    D.quiet--; return missing || [];
                }, function (e) { D.quiet--; throw e; });
            }).then(function (missing) {
                var m = d.meta || {};
                // a copy only when the tab that has it holds it at least as strongly;
                // a weaker hold (a tab that merely reopened it) gives way to this claim
                var fork = (busy[id] || 0) >= (opts.prio || 2);
                D.id = fork ? newId() : id;
                D.auto = !!opts.auto && !fork;
                D.edits = m.edits || 0;
                D.editsAtOpen = D.edits;
                D.editedAt = m.editedAt || d.savedAt || 0;
                D.exportedAt = m.exportedAt || 0;
                rememberOpen(D.id);
                S.claim(D.id, opts.prio || 2).then(function (ok) { if (!ok) forked(D.id, 'claim'); });
                // Nothing is written on a mere open: another tab that has this work and
                // answered late would find it rewritten under it and lose it to a copy on
                // its next save. A copy, though, must exist at once.
                if (fork) {
                    S.schedule(D.id, payload, saved);
                    toast(t('forked'), 'info');
                }
                if (missing.length) toast(t(d.trimmed ? 'trimmed' : 'filesMissing'), 'warning');
                if (opts.auto) showNotice(t('restored').replace(':time', time(m.editedAt || d.savedAt)), true);
                else hideNotice();
                setSaveState('saved');
                track(opts.auto ? 'draft_reopened' : 'draft_open', {
                    age_min: Math.round((d.ageMs || 0) / 60000), files: (d.files || []).length,
                    missing: missing.length, fork: fork ? 1 : 0
                });
                return true;
            });
        }).catch(function (e) {
            trackError(e, 'draft_open');
            if (!opts.auto) toast(t('openFailed'), 'error');
            return false;
        });
    }

    // «New». The work on the page stays in the list, so nothing is lost and nothing
    // needs confirming — except where work cannot be saved at all.
    function reset() {
        var S = store();
        var fresh = function () {
            if (S && D.id) S.release(D.id);
            D.id = null; D.edits = 0; D.editedAt = 0; D.exportedAt = 0;
            D.auto = false;
            rememberOpen(null);
            quietly(o.fresh);
            hideNotice();
            setSaveState('');
            refreshRows();
            track('reset', { drafts: S ? 1 : 0 });
        };
        if (!S) {
            if (!window.confirm(t('confirmReset'))) return;
            fresh();
            return;
        }
        S.flush().then(fresh, fresh);
    }

    function refreshRows() {
        var S = store(); if (!S) return Promise.resolve([]);
        return S.list().then(function (rows) { D.rows = rows; updateCount(); return rows; });
    }
    function updateCount() {
        if (ui.count) ui.count.textContent = D.rows.length ? String(D.rows.length) : '';
    }

    function togglePop(show) {
        var pop = ui.pop, btn = ui.listBtn;
        if (!pop) return;
        if (show == null) show = pop.hidden;
        if (D.stopWatch) { D.stopWatch(); D.stopWatch = null; }
        if (!show) { pop.hidden = true; if (btn) btn.setAttribute('aria-expanded', 'false'); return; }
        var S = store(); if (!S) return;
        // «Open in another tab» is listened for as long as the list is open: a tab in the
        // background answers when the browser lets it run — on a loaded machine that
        // took seconds (18 s once) — and its row says so then.
        var busy = D.busy = {}, shown = false;
        var stop = D.stopWatch = S.watchElsewhere(function (id, prio) {
            if (busy[id] >= prio) return;
            busy[id] = prio;
            if (shown && !pop.hidden) renderList(D.rows, busy);
        });
        // the work on the page is written first, so its row shows it as it is now
        S.flush().then(function () { return S.list(); }).then(function (rows) {
            if (D.stopWatch !== stop) return;      // closed again meanwhile
            D.rows = rows;
            updateCount();
            renderList(rows, busy);
            showUsage(rows);
            shown = true;
            pop.hidden = false;
            if (btn) btn.setAttribute('aria-expanded', 'true');
            track('draft_list_open', { n: rows.length });
        });
    }

    /* «Takes 1.2 GB in this browser · each kept 60 days after its last change» — under
       the list, so the size of what is kept and how long it stays are not a secret. */
    function intlLoc() { try { return window.__intlLocale ? window.__intlLocale() : LOCALE; } catch (_) { return LOCALE; } }
    function fmtUnit(n, unit) {
        try {
            return new Intl.NumberFormat(intlLoc(), { style: 'unit', unit: unit, unitDisplay: unit === 'day' ? 'long' : 'short',
                maximumFractionDigits: unit === 'day' ? 0 : 1 }).format(n);
        } catch (_) {
            return (Math.round(n * 10) / 10) + (unit === 'day' ? ' d' : unit === 'gigabyte' ? ' GB' : ' MB');
        }
    }
    function fmtBytes(b) {
        var mb = (b || 0) / 1048576;
        return mb >= 1024 ? fmtUnit(mb / 1024, 'gigabyte') : fmtUnit(mb < 0.1 ? 0.1 : mb, 'megabyte');
    }
    function budgetChoices() {
        var list = [GIB, 5 * GIB, 20 * GIB];
        if (BUDGET_DEFAULT && list.indexOf(BUDGET_DEFAULT) === -1) list.push(BUDGET_DEFAULT);
        if (BUDGET && list.indexOf(BUDGET) === -1) list.push(BUDGET);
        list.sort(function (a, b) { return a - b; });
        list.push(0);   // no limit
        return list;
    }
    function setBudget(v) {
        var S = store(); if (!S) return;
        var before = BUDGET;
        BUDGET = v;
        try { localStorage.setItem(BUDGET_KEY, String(v)); } catch (_) {}
        track('draft_budget_set', { gb: v ? Math.round(v / GIB) : 0, was: before ? Math.round(before / GIB) : 0 });
        var smaller = v > 0 && (before === 0 || v < before);
        Promise.resolve(S.setBudget ? S.setBudget(v, D.id) : null).then(function () {
            return smaller ? refreshRows() : D.rows;
        }).then(function (rows) { renderList(rows, D.busy); showUsage(rows); }).catch(function () {});
    }
    /* Whether the browser may clear the works by itself: a persisted `work` bucket is never
       evicted (work-storage.js); elsewhere the default storage answers navigator.storage. */
    function protectedNow() {
        var WS = window.__workStorage;
        var ask = WS && WS.protection ? WS.protection().then(function (p) {
            if (p && p.bucket) return !!p.persisted;
            return navigator.storage && navigator.storage.persisted ? navigator.storage.persisted() : null;
        }) : (navigator.storage && navigator.storage.persisted ? navigator.storage.persisted() : Promise.resolve(null));
        return Promise.resolve(ask).catch(function () { return null; });
    }
    function showUsage(rows) {
        var S = store(), pop = ui.pop, host = ui.list;
        if (!S || !S.usage || !pop || !host) return;
        var el = pop.querySelector('.' + cls + '-usage');
        if (!rows.length) { if (el) el.hidden = true; return; }
        Promise.all([S.usage(), protectedNow()]).then(function (r) {
            var u = r[0], safe = r[1];
            if (!el) {
                el = document.createElement('div');
                el.className = cls + '-mini-note ' + cls + '-usage';
                host.parentNode.insertBefore(el, host.nextSibling);
            }
            el.textContent = '';
            var line = document.createElement('span');
            line.textContent = t('usage').replace(':size', fmtBytes(u.bytes)) + ' ';
            el.appendChild(line);
            if (S.setBudget) {
                var lab = document.createElement('label');
                lab.className = cls + '-budget';
                lab.appendChild(document.createTextNode(t('budget') + ' '));
                var sel = document.createElement('select');
                sel.className = cls + '-budget-sel';
                budgetChoices().forEach(function (v) {
                    var op = document.createElement('option');
                    op.value = String(v);
                    op.textContent = v ? fmtBytes(v) : t('budgetUnlimited');
                    if (v === BUDGET) op.selected = true;
                    sel.appendChild(op);
                });
                sel.addEventListener('change', function () { setBudget(+sel.value); });
                lab.appendChild(sel);
                el.appendChild(lab);
                // «Past it, the oldest lose their files» only when there IS an «it»: with «no
                // limit» chosen the line read as a limit nobody had set (#9299, her screenshot).
                if (BUDGET > 0) {
                    var note = document.createElement('span');
                    note.className = cls + '-budget-note';
                    note.textContent = ' ' + t('budgetNote');
                    el.appendChild(note);
                }
            }
            if (safe === true || safe === false) {
                var pr = document.createElement('span');
                pr.className = cls + '-protect' + (safe ? '' : ' warn');
                pr.textContent = ' ' + t(safe ? 'protectedYes' : 'protectedNo');
                el.appendChild(pr);
            }
            el.hidden = false;
        }).catch(function () {});
    }

    function renderList(rows, busy) {
        var host = ui.list;
        if (!host) return;
        if (!rows.length) {
            host.innerHTML = '<p class="' + cls + '-mini-note">' + esc(t('empty')) + '</p>';
            return;
        }
        host.innerHTML = rows.map(function (r) {
            var m = r.meta || {};
            var cur = r.id === D.id, other = !!busy[r.id];
            var status = cur ? t('current')
                : other ? t('otherTab')
                : r.trimmed ? t('trimmedRow')
                : (m.exportedAt && m.exportedAt >= (m.editedAt || 0)) ? t('exported')
                : t('notExported');
            var name = r.label || t('untitled');
            return '<div class="' + cls + '-draft' + (cur ? ' cur' : '') + '">' +
                '<button type="button" class="' + cls + '-draft-open" data-open="' + esc(r.id) + '"' + (cur ? ' aria-current="true"' : '') + '>' +
                    (r.preview ? '<img src="' + esc(r.preview) + '" alt="">' : '<span class="' + cls + '-draft-noimg" aria-hidden="true">' + esc(o.icon || '🗂️') + '</span>') +
                    '<span class="' + cls + '-draft-txt"><span class="' + cls + '-draft-nm">' + esc(name) + '</span>' +
                    '<span class="' + cls + '-draft-sub">' + esc(time(m.editedAt || r.savedAt)) + ' · ' + esc(status) + '</span></span>' +
                '</button>' +
                '<button type="button" class="' + cls + '-draft-del" data-del="' + esc(r.id) + '" title="' + esc(t('del')) + '" aria-label="' + esc(t('del') + ': ' + name) + '"><span aria-hidden="true">🗑️</span></button>' +
            '</div>';
        }).join('');
    }

    function inside(e, el) {
        if (!el) return false;
        var path = e.composedPath ? e.composedPath() : null;
        return path ? path.indexOf(el) !== -1 : el.contains(e.target);
    }

    function bind() {
        var S = store();
        var btn = ui.listBtn;
        if (!S) { if (btn) btn.hidden = true; }
        else if (btn) btn.addEventListener('click', function (e) { e.stopPropagation(); togglePop(); });
        if (ui.newBtn) ui.newBtn.addEventListener('click', function () { reset(); });
        if (ui.list && S) ui.list.addEventListener('click', function (e) {
            var op = e.target.closest('[data-open]'), del = e.target.closest('[data-del]');
            if (op) {
                var id = op.getAttribute('data-open');
                togglePop(false);
                if (id !== D.id) open(id, {});
            } else if (del) {
                var did = del.getAttribute('data-del');
                // the page keeps what it shows; its next change starts a new work
                if (did === D.id) { S.cancelScheduled(); S.release(did); D.id = null; D.auto = false; rememberOpen(null); setSaveState(''); }
                S.remove(did).then(function () { return S.list(); }).then(function (rows) {
                    D.rows = rows; updateCount(); renderList(rows, D.busy); showUsage(rows);
                });
                track('draft_delete');
            }
        });
        if (ui.noteNew) ui.noteNew.addEventListener('click', function () { reset(); });
        if (ui.noteList) ui.noteList.addEventListener('click', function (e) { e.stopPropagation(); togglePop(true); });
        if (ui.noteClose) ui.noteClose.addEventListener('click', hideNotice);
        document.addEventListener('click', function (e) {
            var pop = ui.pop;
            if (pop && !pop.hidden && !inside(e, pop) && !inside(e, btn)) togglePop(false);
        });
        window.addEventListener('keydown', function (e) { if (e.key === 'Escape') togglePop(false); });
    }

    // Arriving. This tab's own work (F5, a tab Chrome restored after a crash) comes
    // back whatever its state; a new tab reopens only an unfinished work that no
    // other tab has open. Resolves once that is decided.
    /* «Мои генерации» → «Открыть» (local-work shelf): the person came for the list of
       works, not for the tool's empty first screen. A one-shot note in localStorage
       (not the address: several tools keep their own state in the hash), for this
       slug only and two minutes at most — a stale note must not pop the list open on
       some later visit. */
    function askedForList() {
        try {
            var a = JSON.parse(localStorage.getItem('td-open-list') || 'null');
            if (!a || a.s !== slug) return false;
            localStorage.removeItem('td-open-list');
            return Date.now() - (+a.t || 0) < 120000;
        } catch (_) { return false; }
    }

    function boot() {
        var S = store();
        bind();
        if (!S) return Promise.resolve();
        D.live = true;
        var wantList = askedForList();
        return S.takeLost().then(function (lost) {
            if (lost.length) {
                showNotice(t('lost'), false);
                track('draft_lost', { n: lost.length });
            }
            // «Is it open in another tab?» is asked once this page has settled and
            // listened to for longer: the tab that has it is not in front, and it
            // answers when the browser lets it run. Measured on prod 30.09: with
            // 250 ms at load time the second tab reopened the first tab's design 1
            // run in 3. An answer later still is handled by forked().
            return whenIdle(2000);
        }).then(function () {
            return Promise.all([S.list(), S.claimedElsewhere(600)]);
        }).then(function (r) {
            var rows = r[0], busy = r[1];
            D.rows = rows;
            updateCount();
            if (D.id || (o.started && o.started())) return;
            var own = lastOpen(), pick = null, prio = 2;
            if (own) pick = rows.filter(function (x) { return x.id === own; })[0] || null;
            // a tab that merely reopened it on arrival (strength 1) gives it back to
            // this one; only a tab that had it open or picked it (2) keeps it
            if (pick && (busy[pick.id] || 0) >= 2) pick = null;
            // by the last EDIT, not the last write: opening writes nothing, so the list
            // order is not «what was worked on last»
            if (!pick && o.reopenUnfinished !== false) {
                prio = 1;
                rows.forEach(function (x) {
                    if (busy[x.id] || !unfinished(x)) return;
                    if (!pick || ((x.meta || {}).editedAt || 0) > ((pick.meta || {}).editedAt || 0)) pick = x;
                });
            }
            if (!pick) return;
            return open(pick.id, { auto: true, prio: prio, busy: busy });
        }).then(function () {
            if (wantList && D.rows.length) { togglePop(true); track('draft_list_from_shelf', { n: D.rows.length }); }
        }).catch(function () {});
    }

    return {
        touch: touch,
        exported: exported,
        adopt: adopt,
        leave: leave,
        boot: boot,
        reset: reset,
        quietly: quietly,
        open: open,
        togglePop: togglePop,
        hideNotice: hideNotice,
        showNotice: showNotice,
        setSaveState: setSaveState,
        id: function () { return D.id; },
        live: function () { return D.live; },
        store: store
    };
}

window.__toolDraft.editor = editor;
})();
